<template>
  <div class="max-w-6xl mx-auto">
    <!-- Header -->
    <div class="flex items-center justify-between mb-8">
      <h1 class="text-3xl font-bold text-gray-900">
        {{ currentTab === "saved" ? "Posts Salvos" : "Posts Curtidos" }}
      </h1>
      <div class="text-sm text-gray-600">
        {{ filteredPosts.length }} post{{
          filteredPosts.length !== 1 ? "s" : ""
        }}
        {{ currentTab === "saved" ? "salvo" : "curtido"
        }}{{ filteredPosts.length !== 1 ? "s" : "" }}
      </div>
    </div>

    <!-- Tabs -->
    <div class="flex border-b border-gray-200 mb-8">
      <button
        @click="switchTab('saved')"
        :class="[
          'px-6 py-3 font-medium text-sm border-b-2 transition-colors',
          currentTab === 'saved'
            ? 'border-primary-500 text-primary-600'
            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
        ]"
      >
        <div class="flex items-center space-x-2">
          <BookmarkIcon class="w-4 h-4" />
          <span>Salvos</span>
        </div>
      </button>
      <button
        @click="switchTab('liked')"
        :class="[
          'px-6 py-3 font-medium text-sm border-b-2 transition-colors',
          currentTab === 'liked'
            ? 'border-primary-500 text-primary-600'
            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
        ]"
      >
        <div class="flex items-center space-x-2">
          <HeartIcon class="w-4 h-4" />
          <span>Curtidos</span>
        </div>
      </button>
    </div>

    <!-- Filters and Search -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <!-- Search -->
        <div class="relative">
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="
              currentTab === 'saved'
                ? 'Buscar nos posts salvos...'
                : 'Buscar nos posts curtidos...'
            "
            class="input-field pl-10 pr-4 py-2 w-full"
          />
          <SearchIcon class="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
        </div>

        <!-- Sort -->
        <div class="relative">
          <select
            v-model="sortBy"
            class="input-field pr-10 py-2 w-full appearance-none"
          >
            <option
              :value="currentTab === 'saved' ? 'saved_date' : 'liked_date'"
            >
              {{
                currentTab === "saved" ? "Data que salvei" : "Data que curti"
              }}
            </option>
            <option value="post_date">Data do post</option>
            <option value="title">Título A-Z</option>
            <option value="popularity">Mais popular</option>
          </select>
          <ArrowUpDownIcon
            class="absolute right-3 top-2.5 h-5 w-5 text-gray-400 pointer-events-none"
          />
        </div>

        <!-- Theme Filter -->
        <div class="relative">
          <select
            v-model="themeFilter"
            class="input-field pr-10 py-2 w-full appearance-none"
          >
            <option value="">Todos os temas</option>
            <option
              v-for="theme in availableThemes"
              :key="theme"
              :value="theme"
            >
              {{ theme }}
            </option>
          </select>
          <FilterIcon
            class="absolute right-3 top-2.5 h-5 w-5 text-gray-400 pointer-events-none"
          />
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex justify-center py-12">
      <div class="spinner"></div>
    </div>

    <!-- Posts Grid -->
    <div v-if="!loading && filteredPosts.length > 0" class="space-y-8">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <article
          v-for="post in paginatedPosts"
          :key="post.id"
          class="post-card cursor-pointer"
          @click="goToPost(post.id)"
        >
          <!-- Post Image -->
          <div
            v-if="post.image_url"
            class="aspect-video bg-gray-200 rounded-t-lg overflow-hidden"
          >
            <img
              :src="post.image_url"
              :alt="post.title"
              class="w-full h-full object-cover"
            />
          </div>
          <div
            v-else
            class="aspect-video bg-gradient-to-br from-primary-100 to-primary-200 rounded-t-lg flex items-center justify-center"
          >
            <FileTextIcon class="w-12 h-12 text-primary-600" />
          </div>

          <!-- Post Content -->
          <div class="p-6">
            <!-- Author -->
            <div class="flex items-center space-x-2 mb-3">
              <UserAvatar :user="post.author" size="xs" />
              <span class="text-sm text-gray-600">{{
                post.author.username
              }}</span>
              <span class="text-sm text-gray-400">•</span>
              <span class="text-sm text-gray-400">{{
                formatDate(post.created_at)
              }}</span>
            </div>

            <!-- Title -->
            <h3 class="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
              {{ post.title }}
            </h3>

            <!-- Content Preview -->
            <p class="text-gray-600 text-sm mb-4 line-clamp-3">
              {{ createContentPreview(post.content, 150) }}
            </p>

            <!-- Saved Indicator (for liked posts that are also saved) -->
            <div
              v-if="currentTab === 'liked' && post.userInteractions?.saved"
              class="flex items-center text-xs text-gray-500 mb-3"
            >
              <BookmarkIcon
                class="w-3 h-3 mr-1 fill-primary-500 text-primary-500"
              />
              <span>Salvo</span>
            </div>

            <!-- Tags/Themes and Stats -->
            <div class="flex items-center justify-between">
              <span
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
              >
                {{ post.theme }}
              </span>

              <!-- Stats -->
              <div class="flex items-center space-x-4 text-sm text-gray-500">
                <div class="flex items-center space-x-1">
                  <HeartIcon
                    class="w-4 h-4"
                    :class="{
                      'fill-red-500 text-red-500': post.userInteractions?.liked,
                    }"
                  />
                  <span>{{ post.stats.likes }}</span>
                </div>
                <div class="flex items-center space-x-1">
                  <MessageCircleIcon class="w-4 h-4" />
                  <span>{{ post.stats.comments }}</span>
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>

      <!-- Load More -->
      <div v-if="hasMorePages" class="text-center">
        <button @click="loadMore" class="btn-secondary" :disabled="loadingMore">
          <span v-if="loadingMore">Carregando...</span>
          <span v-else>Carregar Mais</span>
        </button>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else-if="!loading" class="text-center py-12">
      <component
        :is="currentTab === 'saved' ? BookmarkIcon : HeartIcon"
        class="w-16 h-16 text-gray-400 mx-auto mb-4"
      />
      <h3 class="text-lg font-medium text-gray-900 mb-2">
        {{
          searchQuery || themeFilter
            ? "Nenhum post encontrado"
            : currentTab === "saved"
            ? "Nenhum post salvo"
            : "Nenhum post curtido"
        }}
      </h3>
      <p class="text-gray-600 mb-6">
        {{
          searchQuery || themeFilter
            ? "Tente ajustar os filtros de busca."
            : currentTab === "saved"
            ? "Você ainda não salvou nenhum post. Explore o blog e salve os posts que mais gostar!"
            : "Você ainda não curtiu nenhum post. Explore o blog e curta os posts que mais gostar!"
        }}
      </p>
      <router-link
        to="/"
        class="btn-primary inline-flex items-center space-x-2"
      >
        <CompassIcon class="w-5 h-5" />
        <span>Explorar posts</span>
      </router-link>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/store/auth";
import apiService from "@/services/api";
import { createContentPreview } from "@/utils/contentUtils";
import UserAvatar from "@/components/UserAvatar.vue";
import {
  FileTextIcon,
  BookmarkIcon,
  SearchIcon,
  ArrowUpDownIcon,
  FilterIcon,
  CompassIcon,
  HeartIcon,
  MessageCircleIcon,
} from "lucide-vue-next";

export default {
  name: "SavedPosts",
  components: {
    FileTextIcon,
    BookmarkIcon,
    SearchIcon,
    ArrowUpDownIcon,
    FilterIcon,
    CompassIcon,
    HeartIcon,
    MessageCircleIcon,
    UserAvatar,
  },
  setup() {
    const router = useRouter();
    const authStore = useAuthStore();

    const posts = ref([]);
    const loading = ref(true);
    const loadingMore = ref(false);

    // Tab state
    const currentTab = ref("saved");

    // Filters and sorting
    const searchQuery = ref("");
    const sortBy = ref("saved_date");
    const themeFilter = ref("");
    const currentPage = ref(1);
    const postsPerPage = 12;

    const fetchPosts = async (tab = currentTab.value) => {
      loading.value = true;
      console.log(
        `📡 [SavedPosts] Fazendo requisição para aba "${tab}" - usando handler centralizado`
      );
      try {
        const data = await apiService.get("/api/handlers", {
          params: {
            module: "posts",
            action: tab === "saved" ? "saved" : "liked",
          },
        });
        console.log(`✅ [SavedPosts] Resposta recebida:`, {
          aba: tab,
          posts: data.data.posts.length,
          total: data.data.pagination.total,
        });
        posts.value = data.data.posts;

        // Update sort option when switching tabs
        sortBy.value = tab === "saved" ? "saved_date" : "liked_date";
      } catch (error) {
        console.error(
          `❌ [SavedPosts] Erro ao carregar posts ${
            tab === "saved" ? "salvos" : "curtidos"
          }:`,
          error.response?.data || error.message
        );
      } finally {
        loading.value = false;
      }
    };

    const switchTab = (tab) => {
      currentTab.value = tab;
      searchQuery.value = "";
      themeFilter.value = "";
      currentPage.value = 1;
      fetchPosts(tab);
    };

    // Computed properties for filtering and sorting
    const availableThemes = computed(() => {
      const themes = new Set(posts.value.map((post) => post.theme));
      return Array.from(themes).sort();
    });

    const filteredPosts = computed(() => {
      let filtered = [...posts.value];

      // Filter by search query
      if (searchQuery.value.trim()) {
        const query = searchQuery.value.toLowerCase().trim();
        filtered = filtered.filter(
          (post) =>
            post.title.toLowerCase().includes(query) ||
            post.content.toLowerCase().includes(query) ||
            post.theme.toLowerCase().includes(query) ||
            post.author.username.toLowerCase().includes(query)
        );
      }

      // Filter by theme
      if (themeFilter.value) {
        filtered = filtered.filter((post) => post.theme === themeFilter.value);
      }

      // Sort posts
      filtered.sort((a, b) => {
        switch (sortBy.value) {
          case "post_date":
            return new Date(b.created_at) - new Date(a.created_at);
          case "title":
            return a.title.localeCompare(b.title);
          case "popularity":
            return (
              b.stats.likes +
              b.stats.comments -
              (a.stats.likes + a.stats.comments)
            );
          case "liked_date":
            return (
              new Date(b.liked_at || b.created_at) -
              new Date(a.liked_at || a.created_at)
            );
          case "saved_date":
          default:
            return (
              new Date(b.saved_at || b.created_at) -
              new Date(a.saved_at || a.created_at)
            );
        }
      });

      return filtered;
    });

    const paginatedPosts = computed(() => {
      const start = 0;
      const end = currentPage.value * postsPerPage;
      return filteredPosts.value.slice(start, end);
    });

    const hasMorePages = computed(() => {
      return paginatedPosts.value.length < filteredPosts.value.length;
    });

    const loadMore = () => {
      if (hasMorePages.value && !loadingMore.value) {
        loadingMore.value = true;
        setTimeout(() => {
          currentPage.value++;
          loadingMore.value = false;
        }, 500); // Simulate loading
      }
    };

    const goToPost = (postId) => {
      router.push(`/posts/${postId}`);
    };

    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleDateString("pt-BR", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    };

    onMounted(() => {
      fetchPosts();
    });

    return {
      currentTab,
      authStore,
      posts,
      loading,
      loadingMore,
      searchQuery,
      sortBy,
      themeFilter,
      availableThemes,
      filteredPosts,
      paginatedPosts,
      hasMorePages,
      loadMore,
      goToPost,
      formatDate,
      createContentPreview,
      switchTab,
    };
  },
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
